import * as React from 'react';

import {
  enumZoneStatus,
  enumZoneType,
  ZoneType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  NumberField,
  Select,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { LucideCheck } from 'lucide-react';

import {
  createInventoryZoneAtom,
  updateInventoryZoneAtom,
} from '../../_graphql/mutationInventoryZone';
import { inventoryZonesByFacilityQueryAtom } from '../../_graphql/queryInventoryZones';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryZoneRecord } from '../../types/inventoryZone';

type InventoryZoneDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingZone?: InventoryZoneRecord | null;
};

export const InventoryZoneDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingZone,
}: InventoryZoneDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [facilityId, setFacilityId] = React.useState('');
  const [parentZoneId, setParentZoneId] = React.useState<string>('');
  const [zoneCode, setZoneCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [zoneType, setZoneType] = React.useState<ZoneType>(enumZoneType.Zone);
  const [capacityValue, setCapacityValue] = React.useState<number | null>(null);
  const [capacityUnit, setCapacityUnit] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const $zonesQuery = React.useMemo(
    () =>
      loadable(
        inventoryZonesByFacilityQueryAtom(() => {
          if (!facilityId) {
            return { enabled: false, variables: { facilityId: '' } };
          }
          return { variables: { facilityId } };
        }),
      ),
    [facilityId],
  );

  const zonesResult = useAtomValue($zonesQuery);
  const zonesData =
    zonesResult.state === 'hasData' ? zonesResult.data?.data : null;

  const parentZoneOptions = React.useMemo(() => {
    const zones = zonesData ?? [];
    const filteredZones = editingZone
      ? zones.filter((z) => z.id !== editingZone.id)
      : zones;

    return [
      { key: '', label: t.zonesListView.dialog.noParent },
      ...filteredZones.map((z) => ({
        key: z.id,
        label: `${z.zoneCode} - ${z.name}`,
      })),
    ];
  }, [zonesData, editingZone, t]);

  const createZone = useSetAtom(createInventoryZoneAtom({}));
  const updateZone = useSetAtom(updateInventoryZoneAtom({}));

  const isEditMode = !!editingZone;

  React.useEffect(() => {
    if (editingZone) {
      setFacilityId(editingZone.facilityId);
      setParentZoneId(editingZone.parentZoneId || '');
      setZoneCode(editingZone.zoneCode);
      setName(editingZone.name);
      setZoneType(editingZone.zoneType);
      setCapacityValue(editingZone.capacityValue);
      setCapacityUnit(editingZone.capacityUnit || '');
      setDescription(editingZone.description || '');
    } else {
      setFacilityId('');
      setParentZoneId('');
      setZoneCode('');
      setName('');
      setZoneType(enumZoneType.Zone);
      setCapacityValue(null);
      setCapacityUnit('');
      setDescription('');
    }
  }, [editingZone]);

  const handleClose = React.useCallback(() => {
    setFacilityId('');
    setParentZoneId('');
    setZoneCode('');
    setName('');
    setZoneType(enumZoneType.Zone);
    setCapacityValue(null);
    setCapacityUnit('');
    setDescription('');
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!zoneCode.trim()) {
      toast.error(t.zonesListView.dialog.validation.zoneCodeRequired);
      return;
    }

    if (!name.trim()) {
      toast.error(t.zonesListView.dialog.validation.nameRequired);
      return;
    }

    if (!isEditMode && !facilityId) {
      toast.error(t.zonesListView.dialog.validation.facilityRequired);
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && editingZone) {
        await updateZone({
          id: editingZone.id,
          zoneCode: zoneCode.trim(),
          name: name.trim(),
          zoneType,
          parentZoneId: parentZoneId || null,
          capacityValue: capacityValue,
          capacityUnit: capacityUnit.trim() || null,
          description: description.trim() || null,
        });
        toast.success(t.zonesListView.dialog.updateSuccess);
      } else {
        await createZone({
          facilityId,
          zoneCode: zoneCode.trim(),
          name: name.trim(),
          zoneType,
          status: enumZoneStatus.Active,
          parentZoneId: parentZoneId || undefined,
          capacityValue: capacityValue ?? undefined,
          capacityUnit: capacityUnit.trim() || undefined,
          description: description.trim() || undefined,
        });
        toast.success(t.zonesListView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t.zonesListView.dialog.unknownError;
      toast.error(
        isEditMode
          ? t.zonesListView.dialog.updateError(errorMessage)
          : t.zonesListView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    zoneCode,
    name,
    facilityId,
    zoneType,
    parentZoneId,
    capacityValue,
    capacityUnit,
    description,
    isEditMode,
    editingZone,
    createZone,
    updateZone,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const facilityOptions = React.useMemo(
    () => facilities.map((f) => ({ key: f.id, label: f.name })),
    [facilities],
  );

  const zoneTypeOptions = React.useMemo(
    () => [
      {
        key: enumZoneType.Zone,
        label: t.zonesListView.zoneTypes.Zone,
      },
      {
        key: enumZoneType.Aisle,
        label: t.zonesListView.zoneTypes.Aisle,
      },
      {
        key: enumZoneType.Shelf,
        label: t.zonesListView.zoneTypes.Shelf,
      },
      {
        key: enumZoneType.Bin,
        label: t.zonesListView.zoneTypes.Bin,
      },
      {
        key: enumZoneType.Other,
        label: t.zonesListView.zoneTypes.Other,
      },
    ],
    [t],
  );

  const canSubmit =
    Boolean(zoneCode.trim()) &&
    Boolean(name.trim()) &&
    (isEditMode || Boolean(facilityId)) &&
    !isSubmitting;

  const dialogTitle = isEditMode
    ? t.zonesListView.dialog.editTitle
    : t.zonesListView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.zonesListView.dialog.updating
      : t.zonesListView.dialog.update
    : isSubmitting
      ? t.zonesListView.dialog.creating
      : t.zonesListView.dialog.create;

  return (
    <ConsoleDialog
      className="@InventoryZoneDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <div className="space-y-4">
          {!isEditMode && (
            <Select
              label={t.zonesListView.dialog.facilityLabel}
              value={facilityId}
              onChange={(key) => {
                setFacilityId(String(key));
                setParentZoneId('');
              }}
              options={facilityOptions}
              placeholder={t.zonesListView.dialog.facilityPlaceholder}
              isRequired
            />
          )}

          {isEditMode && (
            <TextField
              label={t.zonesListView.dialog.facilityLabel}
              value={editingZone?.facilityName || ''}
              isDisabled
            />
          )}

          <Select
            label={t.zonesListView.dialog.parentZoneLabel}
            value={parentZoneId}
            onChange={(key) => setParentZoneId(String(key))}
            options={parentZoneOptions}
            placeholder={t.zonesListView.dialog.parentZonePlaceholder}
            isDisabled={!facilityId && !isEditMode}
          />

          <TextField
            label={t.zonesListView.dialog.zoneCodeLabel}
            value={zoneCode}
            onChange={setZoneCode}
            placeholder={t.zonesListView.dialog.zoneCodePlaceholder}
            isRequired
          />

          <TextField
            label={t.zonesListView.dialog.nameLabel}
            value={name}
            onChange={setName}
            placeholder={t.zonesListView.dialog.namePlaceholder}
            isRequired
          />

          <Select
            label={t.zonesListView.dialog.zoneTypeLabel}
            value={zoneType}
            onChange={(key) => setZoneType(key as ZoneType)}
            options={zoneTypeOptions}
            isRequired
          />

          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label={t.zonesListView.dialog.capacityValueLabel}
              value={capacityValue ?? undefined}
              onChange={(value) => setCapacityValue(value ?? null)}
            />
            <TextField
              label={t.zonesListView.dialog.capacityUnitLabel}
              value={capacityUnit}
              onChange={setCapacityUnit}
              placeholder={t.zonesListView.dialog.capacityUnitPlaceholder}
              isDisabled={capacityValue === null}
            />
          </div>

          <TextArea
            label={t.zonesListView.dialog.descriptionLabel}
            value={description}
            onChange={setDescription}
            placeholder={t.zonesListView.dialog.descriptionPlaceholder}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.zonesListView.dialog.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
